import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-hot-toast";
import {
  IndianRupee,
  Wallet,
  Calendar,
  ChevronDown,
  ListOrdered,
  BarChart3,
} from "lucide-react";
import "chart.js/auto";
import { Bar, Doughnut } from "react-chartjs-2";

import useBilling from "../Services/BillingServices/useBilling";
/**
 * ClientBills (New Layout)
 * For a given clientId:
 * - Wallet balance
 * - Interval selector (1,7,30,365)
 * - Bar + Radial (doughnut) charts (Chart.js retained)
 * - “Last 5 Campaign Costs”
 * - Transaction Ledger w/ pagination
 *
 * Endpoints (via GlobalContext.api):
 *  - POST /Whatsapp/v1/getBillingData        { userid, interval, secret }
 *  - POST /Admin/v1.0/getBillingData         { userid, interval, secret }   (insights summary)
 *  - POST /Whatsapp/getBillingLedger         { userid, interval, secret }
 *  - POST /Whatsapp/v1.0/getParentWallet     { userid, secret }
 */

export default function ClientBills() {
  const {getParentWallet, getBillingInsights, getWhatsappBillingData, getWhatsappBillingLedger}=useBilling();

  const { api } = useContext(GlobalContext);
  const { clientId, clientName } = useParams();

  const [interval, setInterval] = useState("30");
  const [walletBalance, setWalletBalance] = useState(null);
  const [insights, setInsights] = useState({});
  const [billingData, setBillingData] = useState({});
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);

  // Charts (dummy visual for trend; keep as in original)
  const [barData, setBarData] = useState(null);
  const [radialData, setRadialData] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil(ledger.length / pageSize));
  const currentRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return ledger.slice(start, start + pageSize);
  }, [ledger, page]);

  // Seed charts
  useEffect(() => {
    setBarData({
      labels: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11"],
      datasets: [
        {
          label: "Sales",
          data: [20, 30, 25, 40, 60, 50, 70, 45, 55, 50, 65],
          backgroundColor: "#01C37D",
          borderRadius: 4,
        },
      ],
    });
    setRadialData({
      labels: ["Sales Increase"],
      datasets: [
        { data: [94, 6], backgroundColor: ["#01C37D", "#e5e7eb"], cutout: "75%" },
      ],
    });
  }, []);

  // API calls
const fetchWallet = async () => {
  if (!clientId) return;

  try {
    const result =
      await getParentWallet({
        userid: clientId,
        secret: "string",
      });

    if (result.success) {
      setWalletBalance(result.data);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed to load wallet");
  }
};


 const fetchInsightsSummary = async (
  intv
) => {
  if (!clientId) return;

  try {
    const result =
      await getBillingInsights({
        userid: clientId,
        interval: intv,
        secret: "string",
      });

    if (result.success) {
      setInsights(result.data);
    }
  } catch {
    // silent (same as before)
  }
};
;

 const fetchBillingData = async (
  intv
) => {
  if (!clientId) return;

  try {
    const result =
      await getWhatsappBillingData({
        userid: clientId,
        interval: intv,
        secret: "string",
      });

    if (result.success) {
      setBillingData(result.data);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed to load billing data");
  }
};


  const fetchLedger = async (intv) => {
  if (!clientId) return;

  try {
    const result =
      await getWhatsappBillingLedger({
        userid: clientId,
        interval: intv,
        secret: "string",
      });

    if (result.success) {
      setLedger(result.data);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed to load ledger");
  }
};


  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchWallet(),
      fetchInsightsSummary(interval),
      fetchBillingData(interval),
      fetchLedger(interval),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, clientId]);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <IndianRupee size={20} />
          </div>
        <div>
            <div className="text-lg font-bold text-gray-900">
              Billing & Ledger — <span className="text-[#01C37D]">{clientName}</span>
            </div>
            <div className="text-xs text-gray-500">Wallet, costs, campaigns, and transactions.</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-gray-100 text-gray-700">
            <Calendar size={16} />
          </div>
          <select
            className="h-9 rounded-lg border px-2 text-sm outline-none"
            value={interval}
            onChange={(e) => {
              setInterval(e.target.value);
              setPage(1);
            }}
          >
            <option value="1">Today</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="365">All Time</option>
          </select>

          <div className="ml-3 inline-flex items-center gap-2 rounded-xl bg-[#01C37D] px-3 py-1.5 text-sm font-semibold text-white">
            <Wallet size={16} />
            {walletBalance == null ? "Loading…" : `₹ ${walletBalance.toFixed(2)}`}
          </div>
        </div>
      </div>

      {/* Top stats + charts */}
      <div className="grid grid-cols-12 gap-3">
        {/* Cards left */}
        <div className="col-span-12 lg:col-span-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border bg-white p-3">
              <div className="text-xs text-gray-500">Amount Spent</div>
              <div className="text-2xl font-bold">
                {insights?.totalCost != null ? `₹ ${Number(insights.totalCost).toFixed(2)}` : "₹ …"}
              </div>
              <div className="text-xs text-gray-400">In Campaign (period)</div>
              {insights?.totalMessages > 0 && (
                <div className="mt-1 text-xs font-semibold text-emerald-600">
                  +{insights.totalMessages} Messages
                </div>
              )}
            </div>
            <div className="rounded-xl border bg-white p-3">
              <div className="text-xs text-gray-500">Cost Per Message</div>
              <div className="text-2xl font-bold">
                {billingData?.averageCost != null
                  ? `₹ ${Number(billingData.averageCost).toFixed(2)}`
                  : "₹ …"}
              </div>
              <div className="text-xs text-gray-400">Marketing Messages</div>
            </div>
          </div>

          {/* Bar + Radial */}
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <BarChart3 size={16} className="text-[#01C37D]" />
              Performance
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 md:col-span-7">
                {barData ? <Bar data={barData} options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: { x: { grid: { display: false } }, y: { display: false } },
                }} /> : <div className="py-8 text-center text-gray-400">Loading…</div>}
              </div>
              <div className="col-span-12 md:col-span-5 flex items-center justify-center">
                {radialData ? <Doughnut data={radialData} options={{
                  responsive: true, plugins: { legend: { display: false } },
                }} /> : <div className="py-8 text-center text-gray-400">Loading…</div>}
              </div>
            </div>
          </div>

          {/* Guide */}
          <div className="rounded-xl border bg-white p-4">
            <div className="text-sm font-semibold text-gray-800">How to Manage Your Billing</div>
            <ul className="mt-2 list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li><span className="font-semibold">Top-up Wallet:</span> keep sufficient balance for uninterrupted campaigns.</li>
              <li><span className="font-semibold">Transaction Ledger:</span> monitor all credits & debits.</li>
            </ul>
          </div>
        </div>

        {/* Last 5 campaign costs */}
        <div className="col-span-12 lg:col-span-3">
          <div className="rounded-xl border bg-white p-3 h-full">
            <div className="mb-2 text-sm font-semibold">Last 5 Campaign Costs</div>
            <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 420 }}>
              {(billingData?.campaignWise || []).slice(-5).map((c, i) => (
                <div key={i} className="rounded-lg border bg-white p-2">
                  <div className="flex items-center justify-between text-sm font-semibold">
                    <span className="text-[#01C37D]">{c.campaignName}</span>
                    <span className="text-rose-600">₹ {Math.abs(parseFloat(c.totalAmoutSpent || 0)).toFixed(2)}</span>
                  </div>
                  <div className="mt-1 border-t pt-1 text-[11px] text-gray-600">
                    Sent: <b>{c.totalMessagesShoot}</b> · Delivered: <b>{c.totalDelivered}</b> · Read: <b>{c.totalRead}</b> · Failed: <b className="text-rose-600">{c.totalFailed}</b> · Success: <b>{c.successRate}</b>
                  </div>
                </div>
              ))}
              {(!billingData?.campaignWise || billingData.campaignWise.length === 0) && (
                <div className="py-8 text-center text-gray-400">No campaign data.</div>
              )}
            </div>
          </div>
        </div>

        {/* Ledger */}
        <div className="col-span-12 lg:col-span-4">
          <div className="rounded-xl border bg-white p-3 h-full">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ListOrdered size={16} className="text-[#01C37D]" />
                Transaction Ledger
              </div>
              <div className="inline-flex items-center gap-1 text-xs text-gray-500">
                Page {page} / {totalPages} <ChevronDown className="rotate-[-90deg]" size={14} />
              </div>
            </div>

            <div className="space-y-2">
              {currentRows.length > 0 ? (
                currentRows.map((txn, idx) => {
                  const isCredit = (txn.transactionType || "").toLowerCase() === "credit";
                  return (
                    <div key={idx} className="rounded-lg bg-gray-50 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm">{new Date(txn.transactionDate).toLocaleDateString()}</div>
                        <div className="text-sm text-gray-600">{txn.narraton || txn.narration || "-"}</div>
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <div className={`text-xs font-bold ${isCredit ? "text-emerald-600" : "text-rose-600"}`}>
                          {txn.transactionType}
                        </div>
                        <div className={`text-sm font-bold ${isCredit ? "text-emerald-700" : "text-rose-700"}`}>
                          ₹ {parseFloat(txn.transactionAmount || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-10 text-center text-gray-400">No data found.</div>
              )}
            </div>

            {ledger.length > 0 && (
              <div className="mt-3 flex items-center justify-center gap-2">
                <button
                  className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    className={`rounded-lg px-3 py-1.5 text-sm ${page === i + 1 ? "bg-[#01C37D] text-white" : "border"}`}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <div className="mt-4 text-center text-xs text-gray-500">Refreshing…</div>}
    </div>
  );
}
